import {
  GP_ACCESS_VERIFICATION_COOKIE,
  type AccessVerificationCookie,
} from '~/plugins/auth/constants';
import {
  discovery,
  randomState,
  buildAuthorizationUrl,
  randomPKCECodeVerifier,
  calculatePKCECodeChallenge,
} from 'openid-client';

// OAuth query parameters that should be stripped from redirect paths
// to prevent accumulation across redirect cycles.
// 'service_error' must also be stripped: if it appears in the URL when login
// is initiated, it gets encoded into the redirect_uri sent to Keycloak.
// Keycloak then rejects the code exchange with "Incorrect redirect_uri" because
// the callback URL it returns to never includes service_error, breaking the
// entire login flow and creating an unrecoverable redirect loop.
const OAUTH_PARAMS = ['state', 'code', 'session_state', 'iss', 'service_error'];

/**
 * Builds a clean redirect path from a route, stripping any OAuth callback
 * query parameters that Keycloak may have appended in a previous redirect.
 */
export const buildCleanRedirectPath = (
  path: string,
  query: Record<string, unknown>,
): string => {
  const cleanQuery = { ...query };
  for (const param of OAUTH_PARAMS) {
    delete cleanQuery[param];
  }

  const entries = Object.entries(cleanQuery).filter(([, v]) => v != null);
  if (entries.length === 0) return path;

  const qs = entries
    .map(
      ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
    )
    .join('&');
  return `${path}?${qs}`;
};

/**
 * The redirect_uri sent to Keycloak for a return path: the path alone, without its query.
 *
 * Keycloak requires the token request's redirect_uri to equal the authorization
 * request's exactly. openid-client's authorizationCodeGrant() derives the token
 * request's from the callback URL with *every* query parameter stripped, and
 * offers no way to pass it (`tokenEndpointParameters` is overwritten). So a
 * query here — `/account/payments?amount=12.34` — failed every code exchange
 * with "Incorrect redirect_uri". The query travels in the verification cookie
 * instead (`return_to`). After the exchange the server plugin hands it to the
 * client plugin, which loads it once auth.init() has stored the session.
 */
export const redirectUriPath = (redirectPath: string): string =>
  redirectPath.split(/[?#]/)[0] || '/';

/**
 * The callback URL to pass to authorizationCodeGrant(): the request's path and
 * query on the public website URL.
 *
 * authorizationCodeGrant() takes the token request's redirect_uri from this URL
 * (query stripped), and Keycloak requires it to equal the authorization
 * request's, which keycloakLoginRedirect() builds on websiteUrl. The request's
 * own URL does not fit: SSR sees http, and behind the load balancer the
 * relaunch build gets its ingress host, not www.g-portal.com.
 */
export const tokenExchangeUrl = (requestUrl: URL, websiteUrl: string): URL =>
  new URL(`${websiteUrl}${requestUrl.pathname}${requestUrl.search}`);

/**
 * The path to load after a successful login, or null to stay on the callback URL.
 *
 * Only when the return path carried a query, since that is the only part the
 * callback URL lost; every other login lands exactly where it did before. Only
 * same-site paths: the cookie is client-writable, so it must not become an
 * open redirect.
 */
export const returnPathAfterLogin = (returnTo: unknown): string | null => {
  if (
    typeof returnTo !== 'string' ||
    !returnTo.startsWith('/') ||
    returnTo.startsWith('//') ||
    returnTo.startsWith('/\\')
  ) {
    return null;
  }

  return returnTo === redirectUriPath(returnTo) ? null : returnTo;
};

/**
 * Performs a PKCE-based login redirect to Keycloak.
 * Sets the verification cookie so the server plugin can exchange the
 * authorization code on callback.
 *
 * Shared between useAuth().login() and the auth route middleware.
 */
export const keycloakLoginRedirect = async (
  redirectPath: string,
  additionalParameters?: Record<string, string>,
) => {
  const config = useRuntimeConfig();

  const codeVerifier = randomPKCECodeVerifier();
  const codeChallenge = await calculatePKCECodeChallenge(codeVerifier);
  const state = randomState();

  const verificationCookie = useCookie<AccessVerificationCookie | undefined>(
    GP_ACCESS_VERIFICATION_COOKIE,
  );
  verificationCookie.value = {
    state,
    // eslint-disable-next-line camelcase
    code_verifier: codeVerifier,
    // eslint-disable-next-line camelcase
    return_to: redirectPath,
  };

  const keycloakIssuer = await discovery(
    new URL(`${config.public.auth.url}/realms/${config.public.auth.realm}`),
    config.public.auth.clientId,
  );

  const parameters: Record<string, string> = {
    ...additionalParameters,
    state,
    // eslint-disable-next-line camelcase
    redirect_uri: `${config.public.websiteUrl}${redirectUriPath(redirectPath)}`,
    scope: 'openid email profile gportal',
    // eslint-disable-next-line camelcase
    code_challenge: codeChallenge,
    // eslint-disable-next-line camelcase
    code_challenge_method: 'S256',
  };

  const redirectTo: URL = buildAuthorizationUrl(keycloakIssuer, parameters);
  return navigateTo(redirectTo.toString(), { external: true });
};
